import React, { useState, useEffect, useCallback } from 'react';
import { GitBranch, Users, ArrowRightLeft } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost } from '../../utils/api';
import OrgTreePanel from './OrgTreePanel';
import MyTeamPanel from './MyTeamPanel';
import MyChainPanel from './MyChainPanel';
import toast from 'react-hot-toast';

export default function TeamOrgPanel() {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('org_tree');
  const [hierarchy, setHierarchy] = useState([]);
  const [myChain, setMyChain] = useState({ chain: [], direct_supervisors: [] });
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const isDirectorOrAbove = ['director', 'admin'].includes(currentUser?.role);
  const isManagerOrAbove = ['manager', 'director', 'admin'].includes(currentUser?.role);

  const tabs = [
    { key: 'org_tree', label: 'Org Tree', icon: GitBranch },
    { key: 'my_team', label: 'My Team', icon: Users },
    { key: 'my_chain', label: 'My Chain', icon: ArrowRightLeft },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [hierarchyRes, chainRes, usersRes] = await Promise.all([
        apiGet('/management/hierarchy'),
        apiGet('/management/my_chain'),
        apiGet('/management/assignable_users'),
      ]);
      if (!hierarchyRes.ok || !chainRes.ok || !usersRes.ok) throw new Error('Failed to load management data');
      const [hierarchyData, chainData, usersData] = await Promise.all([
        hierarchyRes.json(), chainRes.json(), usersRes.json(),
      ]);
      setHierarchy(hierarchyData.hierarchy || []);
      setMyChain(chainData);
      setAssignableUsers(usersData.users || []);
    } catch (err) {
      toast.error('Failed to load management data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAssignReport = async (reporterId, supervisorId) => {
    try {
      const response = await apiPost('/management/assign_report', {
        reporter_id: reporterId,
        supervisor_id: supervisorId,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.errors?.[0] || errData.error || 'Failed to assign report');
      }
      const data = await response.json();
      if (data.already_assigned) {
        toast(data.message, { icon: 'ℹ️' });
      } else {
        toast.success(data.message || 'Reporting relationship created');
      }
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to assign report');
    }
  };

  const handleRemoveReport = async (reporterId, supervisorId) => {
    try {
      const response = await apiPost('/management/remove_report', {
        reporter_id: reporterId,
        supervisor_id: supervisorId,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.errors?.[0] || errData.error || 'Failed to remove report');
      }
      toast.success('Reporting relationship removed');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to remove report');
    }
  };

  const handleBulkAssign = async (reporterIds, supervisorId) => {
    try {
      const response = await apiPost('/management/bulk_assign', {
        reporter_ids: reporterIds,
        supervisor_id: supervisorId,
      });
      if (!response.ok) throw new Error('Failed to bulk assign');
      const data = await response.json();
      if (data.created?.length > 0) {
        toast.success(`${data.created.length} user(s) assigned`);
      }
      if (data.errors?.length > 0) {
        data.errors.forEach(e => toast.error(e));
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to bulk assign');
    }
  };

  const handleMoveReport = async (reporterId, fromSupervisorId, toSupervisorId) => {
    try {
      const response = await apiPost('/management/move_report', {
        reporter_id: reporterId,
        from_supervisor_id: fromSupervisorId,
        to_supervisor_id: toSupervisorId,
      });
      if (!response.ok) throw new Error('Failed to move user');
      toast.success('User moved successfully');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to move user');
    }
  };

  return (
    <div>
      {/* Sub-tabs */}
      <div className={`flex gap-1 mb-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-rasa font-medium rounded-t-lg transition-colors ${
                activeTab === tab.key
                  ? `${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-blue-600'} border-b-2 border-blue-600`
                  : `${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'org_tree' && (
        <OrgTreePanel
          hierarchy={hierarchy}
          loading={loading}
          onAssignReport={handleAssignReport}
          onRemoveReport={handleRemoveReport}
          onBulkAssign={handleBulkAssign}
          onMoveReport={handleMoveReport}
          assignableUsers={assignableUsers}
          isDirectorOrAbove={isDirectorOrAbove}
          currentUser={currentUser}
          theme={theme}
        />
      )}
      {activeTab === 'my_team' && (
        <MyTeamPanel
          hierarchy={hierarchy}
          currentUser={currentUser}
          onAssignReport={isManagerOrAbove ? handleAssignReport : undefined}
          onRemoveReport={isManagerOrAbove ? handleRemoveReport : undefined}
          assignableUsers={assignableUsers}
          isDirectorOrAbove={isDirectorOrAbove}
          theme={theme}
        />
      )}
      {activeTab === 'my_chain' && (
        <MyChainPanel
          chain={myChain.chain || []}
          directSupervisors={myChain.direct_supervisors || []}
          currentUser={currentUser}
          theme={theme}
        />
      )}
    </div>
  );
}
